import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Send, Eye, UserPlus, Sparkles } from "lucide-react";
import ImagePicker from "../components/ImagePicker";

const sectionKey = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `s-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const withKey = (s) => ({ _key: sectionKey(), ...s });

export default function Newsletter() {
  const [meta, setMeta] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState("All");
  const CATEGORIES = ["All", "All Clients", "Buyer prospects", "Seller prospects", "Real Estate Agents", "Open House Attendees", "Past Clients"];
  const [selected, setSelected] = useState(new Set());
  const [extra, setExtra] = useState("");
  const [preview, setPreview] = useState("");
  const [sending, setSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiBusy, setAiBusy] = useState(false);

  const [draft, setDraft] = useState({
    subject: "This Month at CORE",
    intro: "Hi neighbor,\n\nHere's what's happening in Metro Atlanta real estate this month, plus a few tips from our desk to yours.",
    sections: [
      withKey({ heading: "Market Snapshot", body: "Median sale price in Snellville rose 3.2% over last quarter. Inventory is up 11% — good news for buyers, still a strong story for sellers who stage & price right.", image_url: "", cta_label: "", cta_url: "" }),
      withKey({ heading: "Tip of the Month", body: "Thinking of listing in spring? Start photography prep NOW — light matters, and Thursday-noon MLS launches capture the weekend traffic wave.", image_url: "", cta_label: "", cta_url: "" }),
    ],
    cta_label: "Schedule a free 15-min consult",
    cta_url: "https://corerealestatetools.com",
  });

  const loadTemplate = (tpl) => {
    if (tpl === "follow-up") {
      setDraft({
        subject: "Great connecting with you at our open house",
        intro: "Hi {{first_name}},\n\nThank you for stopping by our open house — it was a pleasure meeting you. I wanted to send a quick note with the listing details and a few thoughts based on what we discussed.",
        sections: [
          withKey({ heading: "About the home", body: "A quick recap of the listing you toured: highlights, key specs, and what makes this property stand out in today's Metro Atlanta market.", image_url: "", cta_label: "View full listing", cta_url: "https://corerealestatetools.com" }),
          withKey({ heading: "What's next?", body: "If you'd like a private second showing, comps for a comparable home, or a no-pressure buyer consultation, I'm one reply away. Happy to move at your pace.", image_url: "", cta_label: "Schedule a call", cta_url: "https://corerealestatetools.com" }),
        ],
        cta_label: "Reply to book a private showing",
        cta_url: "mailto:info@corerealtyandassociates.com",
      });
      toast.success("Follow-up template loaded");
    } else {
      toast.info("Monthly template already loaded");
    }
  };

  useEffect(() => {
    api.get("/newsletter/meta").then(r => setMeta(r.data));
    api.get("/contacts").then(r => setContacts(r.data || []));
  }, []);

  const updateSection = (i, field, val) =>
    setDraft(d => ({ ...d, sections: d.sections.map((s, idx) => idx === i ? { ...s, [field]: val } : s) }));
  const addSection = () => setDraft(d => ({ ...d, sections: [...d.sections, withKey({ heading: "", body: "", image_url: "", cta_label: "", cta_url: "" })] }));
  const removeSection = (i) => setDraft(d => ({ ...d, sections: d.sections.filter((_, idx) => idx !== i) }));

  const toggle = (email) => {
    const s = new Set(selected);
    if (s.has(email)) s.delete(email); else s.add(email);
    setSelected(s);
  };
  const filteredContacts = useMemo(
    () => (categoryFilter === "All"
      ? contacts
      : contacts.filter(c => (c.category || "All Clients") === categoryFilter)),
    [contacts, categoryFilter]
  );
  const toggleAll = () => {
    const emails = filteredContacts.map(c => c.email);
    const allSelected = emails.length > 0 && emails.every(e => selected.has(e));
    const s = new Set(selected);
    if (allSelected) emails.forEach(e => s.delete(e));
    else emails.forEach(e => s.add(e));
    setSelected(s);
  };

  const recipients = useMemo(() => {
    const extras = extra.split(/[,\n]/).map(s => s.trim()).filter(s => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(s));
    return Array.from(new Set([...selected, ...extras]));
  }, [selected, extra]);

  const doPreview = async () => {
    try {
      const { data } = await api.post("/newsletter/render", { ...draft, recipients: [] });
      setPreview(data.html);
      setShowPreview(true);
    } catch { toast.error("Preview failed"); }
  };

  const sendTest = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/newsletter/send", { ...draft, recipients: ["info@corerealtyandassociates.com"] });
      toast.success(`Test sent (${data.sent}/${data.total})`);
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };

  const sendAll = async () => {
    if (recipients.length === 0) return toast.error("No recipients selected");
    if (!window.confirm(`Send to ${recipients.length} recipient(s)?`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/newsletter/send", { ...draft, recipients });
      toast.success(`Sent to ${data.sent}/${data.total}`);
      if (data.failed?.length) toast.error(`${data.failed.length} failed — check console`);
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };

  const aiDraft = async () => {
    if (!aiPrompt.trim()) return toast.error("Give me a topic first");
    setAiBusy(true);
    try {
      const { data } = await api.post("/newsletter/ai-draft", { prompt: aiPrompt });
      setDraft({
        subject: data.subject || draft.subject,
        intro: data.intro || draft.intro,
        sections: (data.sections || []).map(s => withKey({ heading: s.heading || "", body: s.body || "", image_url: s.image_url || "" })),
        cta_label: data.cta_label || draft.cta_label,
        cta_url: data.cta_url || draft.cta_url,
      });
      toast.success("AI draft loaded — edit as you like");
      setAiOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "AI draft failed"); }
    finally { setAiBusy(false); }
  };

  return (
    <div className="space-y-8" data-testid="newsletter-page">
      <header className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Brokerage · Marketing</div>
          <h1 className="font-heading text-5xl mt-2">Monthly Newsletter Builder</h1>
          <p className="text-sm text-[#050303]/70 mt-2">Compose, preview, and send a branded newsletter to your clients & contacts. Header + footer are locked to CORE branding.</p>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="overline">Template:</span>
            <button onClick={() => loadTemplate("monthly")} className="px-3 py-1.5 rounded-full text-xs font-semibold border border-[#d1cece] hover:bg-[#ffffff]" data-testid="tpl-monthly">Monthly Newsletter</button>
            <button onClick={() => loadTemplate("follow-up")} className="px-3 py-1.5 rounded-full text-xs font-semibold border border-[#d1cece] hover:bg-[#ffffff]" data-testid="tpl-follow-up">Open House Follow-Up</button>
          </div>
        </div>
        <button
          onClick={() => setAiOpen(o => !o)}
          className="bg-[#bf858e] text-white pill-btn hover:bg-[#050303] flex items-center gap-2 self-start"
          data-testid="nl-ai-toggle"
        >
          <Sparkles size={16} /> AI Draft
        </button>
      </header>

      {aiOpen && (
        <div className="core-card p-6" data-testid="nl-ai-panel">
          <div className="overline mb-2">Powered by Claude Sonnet 4.5</div>
          <p className="text-xs text-[#050303]/60 mb-3">One-line brief. Claude will generate subject, intro, 2–4 sections, and a CTA. You can edit anything after.</p>
          <textarea
            value={aiPrompt} onChange={e => setAiPrompt(e.target.value)}
            rows={2}
            placeholder="e.g. January Snellville market update with a probate-planning tip"
            className="w-full p-3 border border-[#d1cece] rounded-lg outline-none focus:border-[#bf858e]"
            data-testid="nl-ai-prompt"
          />
          <div className="mt-3 flex gap-2">
            <button
              onClick={aiDraft} disabled={aiBusy || !aiPrompt.trim()}
              className="bg-[#050303] text-white pill-btn text-sm disabled:opacity-50 flex items-center gap-2"
              data-testid="nl-ai-generate"
            >
              <Sparkles size={14} /> {aiBusy ? "Drafting…" : "Generate Draft"}
            </button>
            <button onClick={() => setAiOpen(false)} className="border border-[#050303] pill-btn text-sm" data-testid="nl-ai-cancel">Cancel</button>
          </div>
          <div className="text-xs text-[#050303]/50 mt-3">⚠️ Loading a new draft will overwrite the fields you have now.</div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Editor */}
        <div className="lg:col-span-2 core-card p-7 space-y-5" data-testid="newsletter-editor">
          <div>
            <label className="overline">Subject Line</label>
            <input value={draft.subject} onChange={e => setDraft(d => ({ ...d, subject: e.target.value }))}
              className="w-full mt-2 px-4 py-3 border border-[#d1cece] rounded-lg outline-none focus:border-[#bf858e] font-heading text-lg"
              data-testid="nl-subject" />
          </div>
          <div>
            <label className="overline">Intro Paragraph</label>
            <textarea rows={4} value={draft.intro} onChange={e => setDraft(d => ({ ...d, intro: e.target.value }))}
              className="w-full mt-2 px-4 py-3 border border-[#d1cece] rounded-lg outline-none focus:border-[#bf858e]"
              data-testid="nl-intro" />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="overline">Sections</label>
              <button onClick={addSection} className="text-xs font-semibold text-[#bf858e] hover:text-[#050303] flex items-center gap-1" data-testid="nl-add-section">
                <Plus size={13} /> Add section
              </button>
            </div>
            <div className="space-y-4">
              {draft.sections.map((s, i) => (
                <div key={s._key || i} className="border border-[#d1cece] rounded-lg p-4 bg-[#ffffff]" data-testid={`nl-section-${i}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs uppercase tracking-wider text-[#050303]/60">Section {i + 1}</span>
                    <button onClick={() => removeSection(i)} className="text-[#050303]/40 hover:text-[#bf858e]" data-testid={`nl-remove-${i}`}><Trash2 size={13} /></button>
                  </div>
                  <input placeholder="Heading" value={s.heading} onChange={e => updateSection(i, "heading", e.target.value)}
                    className="w-full mb-2 px-3 py-2 border border-[#d1cece] rounded-md" data-testid={`nl-heading-${i}`} />
                  <textarea placeholder="Body copy" rows={3} value={s.body} onChange={e => updateSection(i, "body", e.target.value)}
                    className="w-full mb-2 px-3 py-2 border border-[#d1cece] rounded-md" data-testid={`nl-body-${i}`} />
                  <ImagePicker value={s.image_url} onChange={(v) => updateSection(i, "image_url", v)} testIdPrefix={`nl-img-${i}`} />
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    <input placeholder="Section CTA label (optional)" value={s.cta_label || ""} onChange={e => updateSection(i, "cta_label", e.target.value)}
                      className="px-3 py-2 border border-[#d1cece] rounded-md text-xs" data-testid={`nl-section-cta-label-${i}`} />
                    <input placeholder="Section CTA URL" value={s.cta_url || ""} onChange={e => updateSection(i, "cta_url", e.target.value)}
                      className="px-3 py-2 border border-[#d1cece] rounded-md text-xs" data-testid={`nl-section-cta-url-${i}`} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="overline">CTA Label</label>
              <input value={draft.cta_label} onChange={e => setDraft(d => ({ ...d, cta_label: e.target.value }))}
                className="w-full mt-2 px-3 py-2 border border-[#d1cece] rounded-md" data-testid="nl-cta-label" />
            </div>
            <div>
              <label className="overline">CTA URL</label>
              <input value={draft.cta_url} onChange={e => setDraft(d => ({ ...d, cta_url: e.target.value }))}
                className="w-full mt-2 px-3 py-2 border border-[#d1cece] rounded-md" data-testid="nl-cta-url" />
            </div>
          </div>

          <div className="pt-4 border-t border-[#d1cece] flex flex-wrap gap-2">
            <button onClick={doPreview} className="border border-[#050303] pill-btn text-sm flex items-center gap-2" data-testid="nl-preview-btn"><Eye size={14} /> Preview</button>
            <button onClick={sendTest} disabled={sending} className="border border-[#bf858e] text-[#bf858e] pill-btn text-sm disabled:opacity-50" data-testid="nl-test-btn">Send Test → info@</button>
            <button onClick={sendAll} disabled={sending || recipients.length === 0} className="bg-[#050303] text-white pill-btn text-sm flex items-center gap-2 hover:bg-[#bf858e] disabled:opacity-50" data-testid="nl-send-btn">
              <Send size={14} /> Send to {recipients.length} recipient{recipients.length === 1 ? "" : "s"}
            </button>
          </div>
        </div>

        {/* Recipients */}
        <div className="core-card p-7 space-y-4" data-testid="newsletter-recipients">
          <div>
            <div className="overline">Contact List</div>
            <div className="flex items-center justify-between mt-1 gap-2 flex-wrap">
              <h3 className="font-heading text-xl">
                {filteredContacts.length} contact{filteredContacts.length === 1 ? "" : "s"}
                {categoryFilter !== "All" && <span className="text-sm text-[#050303]/50"> · {categoryFilter}</span>}
              </h3>
              {filteredContacts.length > 0 && (
                <button onClick={toggleAll} className="text-xs uppercase tracking-wider text-[#bf858e] hover:text-[#050303]" data-testid="nl-toggle-all">
                  {filteredContacts.every(c => selected.has(c.email)) ? "Unselect all" : "Select all"}
                </button>
              )}
            </div>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="mt-3 w-full px-3 py-2 text-sm border border-[#d1cece] rounded-md bg-white focus:outline-none focus:border-[#bf858e]"
              data-testid="nl-category-filter"
            >
              {CATEGORIES.map(c => <option key={c} value={c}>{c === "All" ? "All categories" : c}</option>)}
            </select>
          </div>

          <NewContactForm onCreated={(c) => setContacts(cs => [...cs, c].sort((a, b) => a.name.localeCompare(b.name)))} />

          <div className="max-h-64 overflow-y-auto space-y-1 border-t border-[#d1cece] pt-3">
            {filteredContacts.length === 0 && (
              <div className="text-xs text-[#050303]/50 py-2">
                {categoryFilter === "All" ? "Add your first contact above." : `No contacts tagged "${categoryFilter}" yet.`}
              </div>
            )}
            {filteredContacts.map(c => (
              <label key={c.id} className="flex items-center gap-2 py-1.5 cursor-pointer" data-testid={`contact-${c.id}`}>
                <input type="checkbox" checked={selected.has(c.email)} onChange={() => toggle(c.email)} className="accent-[#050303]" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate">{c.name}</div>
                  <div className="text-xs text-[#050303]/55 truncate">{c.email} · <span className="text-[#050303]/40">{c.category || "All Clients"}</span></div>
                </div>
                <button onClick={(e) => { e.preventDefault(); api.delete(`/contacts/${c.id}`).then(() => setContacts(cs => cs.filter(x => x.id !== c.id))); }}
                  className="text-[#050303]/40 hover:text-[#bf858e]" data-testid={`del-contact-${c.id}`}>
                  <Trash2 size={12} />
                </button>
              </label>
            ))}
          </div>

          <div>
            <label className="overline">Extra emails (comma or newline separated)</label>
            <textarea rows={2} value={extra} onChange={e => setExtra(e.target.value)}
              placeholder="jane@example.com, bob@example.com"
              className="w-full mt-2 px-3 py-2 border border-[#d1cece] rounded-md text-sm"
              data-testid="nl-extra-emails" />
          </div>
        </div>
      </div>

      {meta && (
        <section className="core-card p-6 text-xs text-[#050303]/70" data-testid="nl-footer-preview">
          <div className="overline mb-2">Locked Footer (auto-included on every send)</div>
          <div><strong>CORE Realty and Associates LLC</strong> · {meta.address}</div>
          <div>{meta.email} · {meta.phone}</div>
          <div className="mt-1">{meta.socials.map(s => s.name).join(" · ")}</div>
        </section>
      )}

      {showPreview && (
        <div className="fixed inset-0 bg-[#050303]/70 z-50 flex items-start justify-center p-6 overflow-y-auto" onClick={() => setShowPreview(false)} data-testid="nl-preview-modal">
          <div className="bg-white rounded-lg max-w-2xl w-full my-8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-[#d1cece]">
              <h3 className="font-heading text-xl">Newsletter Preview</h3>
              <button onClick={() => setShowPreview(false)} className="text-[#050303]/60 hover:text-[#050303]" data-testid="nl-close-preview">✕</button>
            </div>
            <iframe title="preview" srcDoc={preview} className="w-full" style={{ height: "70vh", border: 0 }} />
          </div>
        </div>
      )}
    </div>
  );
}

function NewContactForm({ onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", tag: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/contacts", form);
      onCreated(data);
      setForm({ name: "", email: "", tag: "" });
      toast.success("Contact added");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <form onSubmit={submit} className="space-y-2" data-testid="new-contact-form">
      <input required placeholder="Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
        className="w-full px-3 py-2 border border-[#d1cece] rounded-md text-sm" data-testid="contact-name" />
      <input required type="email" placeholder="Email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
        className="w-full px-3 py-2 border border-[#d1cece] rounded-md text-sm" data-testid="contact-email" />
      <button type="submit" className="w-full bg-[#050303] text-white pill-btn text-xs flex items-center justify-center gap-1" data-testid="add-contact-btn">
        <UserPlus size={13} /> Add contact
      </button>
    </form>
  );
}
